import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  X, Search, CheckSquare, Calendar, StickyNote, Cloud,
  Music, Tv, Film, Book, Image, Mail, Bookmark,
  Rss, MessageSquare, Trophy, Waves, Wind, Sparkles,
  Scale, Dumbbell
} from 'lucide-react'
import { appAPI, widgetAPI } from '../services/api'
import { useDashboardStore } from '../stores/dashboardStore'
import { useModalClose } from '../hooks/useModalClose'
import toast from 'react-hot-toast'

interface AddWidgetModalProps {
  onClose: () => void
  onAdd: () => void
}

const getWidgetIcon = (slug: string) => {
  const iconMap: { [key: string]: any } = {
    'google-search': Search,
    'chatgpt-search': Sparkles,
    'tasks': CheckSquare,
    'calendar': Calendar,
    'notes': StickyNote,
    'weather': Cloud,
    'spotify': Music,
    'music': Music,
    'tv-series': Tv,
    'new-movies': Film,
    'new-shows': Tv,
    'books-genre': Book,
    'books': Book,
    'photo-albums': Image,
    'photos': Image,
    'email': Mail,
    'bookmarks': Bookmark,
    'rss': Rss,
    'rss-reader': Rss,
    'reddit': MessageSquare,
    'reddit-widget': MessageSquare,
    'sports': Trophy,
    'sports-scores': Trophy,
    'wind': Wind,
    'wind-conditions': Wind,
    'tide': Waves,
    'tide-predictions': Waves,
    'weight-tracker': Scale,
    'fitness-tracker': Dumbbell,
  }
  
  return iconMap[slug] || Sparkles
}

export default function AddWidgetModal({ onClose, onAdd }: AddWidgetModalProps) {
  const { currentTabId, widgets } = useDashboardStore()
  const [selectedApp, setSelectedApp] = useState<any>(null)
  const { handleBackdropClick } = useModalClose(onClose)

  const { data: apps, isLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: async () => {
      const { data } = await appAPI.getAll()
      return data
    },
  })

  const handleAddWidget = async () => {
    if (!selectedApp || !currentTabId) return

    try {
      // Calculate the bottom-most position to add new widgets at the bottom
      let maxY = 0
      if (widgets && widgets.length > 0) {
        widgets.forEach(widget => {
          const bottomY = widget.position_y + widget.height
          if (bottomY > maxY) {
            maxY = bottomY
          }
        })
      }

      await widgetAPI.create(currentTabId, {
        app_slug: selectedApp.slug,
        position_x: 0,
        position_y: maxY, // Add at the bottom
        width: 4,
        height: 3,
      })

      toast.success('Widget added!')
      onAdd()
    } catch (error) {
      toast.error('Failed to add widget')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleBackdropClick}>
      <div className="rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-2xl" style={{ backgroundColor: 'var(--color-widget-bg)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Add Widget</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4" style={{ color: 'var(--color-text-secondary)' }}>Loading apps...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {apps?.map((app: any) => {
                const IconComponent = getWidgetIcon(app.slug)
                return (
                  <button
                    key={app.id}
                    onClick={() => setSelectedApp(app)}
                    className={`p-4 border-2 rounded-xl text-left transition hover:shadow-lg ${
                      selectedApp?.id === app.id
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {app.icon_url && (app.icon_url.startsWith('http://') || app.icon_url.startsWith('https://')) ? (
                        <img src={app.icon_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : app.icon_url ? (
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-3xl ${
                          selectedApp?.id === app.id ? 'bg-primary-100' : 'bg-gray-100'
                        }`}>
                          {app.icon_url}
                        </div>
                      ) : (
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          selectedApp?.id === app.id ? 'bg-primary-100' : 'bg-gray-100'
                        }`}>
                          <IconComponent 
                            size={24} 
                            className={selectedApp?.id === app.id ? 'text-primary-600' : 'text-gray-600'}
                          />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>{app.name}</h3>
                        <p className="text-sm line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>{app.description}</p>
                        <span className="inline-block mt-2 text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}>
                          {app.category}
                        </span>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="px-6 py-2 hover:bg-gray-100 rounded-lg transition"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleAddWidget}
            disabled={!selectedApp}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Widget
          </button>
        </div>
      </div>
    </div>
  )
}

